import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Pagination,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Print as PrintIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  LocalPharmacy as MedicationIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useResidents } from "../hooks/useResidents";
import { useMedications } from "../hooks/useMedications";

// Define Medication Display Interface for the dialog
interface MedicationDisplay {
  id: string;
  medicationName?: string;
  dosage?: string;
  frequency?: string;
  route?: string;
  startDate?: string;
  status?: string;
}

const ResidentsPage: React.FC = () => {
  const navigate = useNavigate();

  const {
    residents,
    loading,
    error,
    totalElements,
    totalPages,
    currentPage,
    fetchResidents,
    searchResidents,
    dischargeResident,
  } = useResidents();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [viewingMedicationId, setViewingMedicationId] = useState<string | null>(
    null
  );
  const [viewingOrder, setViewingOrder] = useState<any | null>(null);

  useEffect(() => {
    fetchResidents(0, rowsPerPage);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim()) {
        searchResidents(searchTerm, 0, rowsPerPage);
      } else {
        fetchResidents(0, rowsPerPage);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredResidents = residents.filter((resident) => {
    const matchesStatus =
      statusFilter === "All" || resident.status === statusFilter;
    const matchesType =
      typeFilter === "All" || (resident as any).careType === typeFilter;
    return matchesStatus && matchesType;
  });

  const viewingResident = viewingId
    ? residents.find((r) => r.id === viewingId)
    : null;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewDetails = () => {
    const res = residents.find((r) => r.id === selectedRow);
    // Map available resident fields to the EMR patient banner shape
    const patientDetails = res
      ? {
          name: `${res.firstName} ${res.middleName ? res.middleName + " " : ""}${
            res.lastName
          }`.trim(),
          EMRNo:
            (res as any).medicareNumber ||
            (res as any).medicaidNumber ||
            res.id ||
            "N/A",
          bp: (res as any).bp || "N/A",
          weight: (res as any).weight || "N/A",
          height: (res as any).height || "N/A",
          bmi: (res as any).bmi || "N/A",
          room:
            res.roomNumber && res.bedNumber
              ? `${res.roomNumber}-${res.bedNumber}`
              : res.roomNumber || "Not Assigned",
          sex: (res as any).gender || "N/A",
          dob: res.dateOfBirth || "N/A",
          attendingPhysician:
            (res as any).attendingPhysicianName ||
            (res as any).attendingPhysician ||
            "N/A",
          allergies: (res as any).allergies
            ? String((res as any).allergies)
                .split(",")
                .map((a: string) => a.trim())
                .filter(Boolean)
            : [],
        }
      : undefined;

    navigate("/clinical-notes", {
      state: {
        residentId: selectedRow,
        defaultTab: "medication",
        patientDetails,
      },
    });
    handleMenuClose();
  };

  const handleDiagnosis = () => {
    navigate("/patient-diagnosis", { state: { residentId: selectedRow } });
    handleMenuClose();
  };

  const handleEdit = () => {
    navigate("/admissions", {
      state: {
        mode: "edit",
        residentId: selectedRow,
      },
    });
    handleMenuClose();
  };

  const handleDischarge = async () => {
    if (!selectedRow) return;
    const reason = window.prompt("Enter discharge reason:");
    if (reason) {
      try {
        await dischargeResident(selectedRow, reason);
        fetchResidents(page - 1, rowsPerPage);
      } catch (err) {
        console.error("Failed to discharge resident:", err);
      }
    }
    handleMenuClose();
  };

  const handlePageChange = (event: unknown, value: number) => {
    setPage(value);
    fetchResidents(value - 1, rowsPerPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    const newSize = Number(event.target.value);
    setRowsPerPage(newSize);
    setPage(1);
    fetchResidents(0, newSize);
  };

  const calculateAge = (dob: string): number => {
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  };

  if (loading && residents.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleOpenCarePlan = (resident: any) => {
    setViewingOrder({
      name: `${resident.firstName} ${
        resident.middleName ? resident.middleName + " " : ""
      }${resident.lastName}`,
      attendingPhysician: resident.attendingPhysician || "N/A",
      status:
        resident.status === "ACTIVE"
          ? "Active"
          : resident.status === "PENDING"
          ? "Pending"
          : "Other",
      carePlan: (resident as any).carePlan || {},
    });
    setViewingId(resident.id || null);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Residents
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={Link}
          to="/admissions"
        >
          Add New Resident
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          variant="outlined"
          placeholder="Search residents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 250 }}
        />

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as string)}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DISCHARGED">Discharged</MenuItem>
            <MenuItem value="ON_LEAVE">On Leave</MenuItem>
            <MenuItem value="TRANSFERRED">Transferred</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value as string)}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="SNF">SNF</MenuItem>
            <MenuItem value="HOSPICE">Hospice</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => {
            setSearchTerm("");
            setStatusFilter("All");
            setTypeFilter("All");
            fetchResidents(0, rowsPerPage);
          }}
        >
          Reset
        </Button>
      </Paper>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Date of Birth</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Room</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Care Level</TableCell>
              <TableCell>Admission Date</TableCell>
              <TableCell>Care Plan</TableCell>
              {/* <TableCell>Medication</TableCell> */}
              <TableCell>Print</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredResidents.length > 0 ? (
              filteredResidents.map((resident) => (
                <TableRow
                  key={resident.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {resident.firstName}{" "}
                    {resident.middleName ? resident.middleName + " " : ""}
                    {resident.lastName}
                  </TableCell>
                  <TableCell>{resident.dateOfBirth}</TableCell>
                  <TableCell>{calculateAge(resident.dateOfBirth)}</TableCell>
                  <TableCell>
                    {resident.roomNumber
                      ? resident.bedNumber
                        ? `${resident.roomNumber}-${resident.bedNumber}`
                        : resident.roomNumber
                      : "Not Assigned"}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const rawStatus =
                        resident.status ||
                        (resident as any).residentStatus ||
                        (resident as any).statusText ||
                        "N/A";
                      const normalized = String(rawStatus).toUpperCase();
                      const statusColor =
                        normalized === "ACTIVE"
                          ? "success"
                          : normalized === "ON_LEAVE"
                          ? "warning"
                          : normalized === "DISCHARGED"
                          ? "info"
                          : normalized === "TRANSFERRED"
                          ? "secondary"
                          : normalized === "DECEASED"
                          ? "error"
                          : "default";
                      return (
                        <Chip
                          label={rawStatus}
                          size="small"
                          color={statusColor as any}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>{resident.careLevel || "N/A"}</TableCell>
                  <TableCell>{resident.admissionDate}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Tooltip title="View Care Plan">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenCarePlan(resident)}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  {/* <TableCell sx={{ textAlign: "center" }}>
                    <Tooltip title="View Medication">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          navigate("/clinical-notes", {
                            state: {
                              residentId: resident.id,
                              defaultTab: "medication",
                            },
                          })
                        }
                      >
                        <MedicationIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell> */}
                  <TableCell sx={{ textAlign: "center" }}>
                    <Tooltip title="Print">
                      <IconButton onClick={() => window.print()}>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(event) =>
                        handleMenuOpen(event, resident.id || "")
                      }
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  {loading ? "Loading residents..." : "No residents found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
        />
        <FormControl sx={{ minWidth: 120, ml: 2 }}>
          <InputLabel>Rows per page</InputLabel>
          <Select
            value={rowsPerPage}
            label="Rows per page"
            onChange={handleRowsPerPageChange}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>Edit Resident</MenuItem>
        <MenuItem onClick={handleDiagnosis}>Patient Summary</MenuItem>
        <MenuItem onClick={handleViewDetails}>Medication</MenuItem>
        <MenuItem onClick={handleDiagnosis}>Physician Order</MenuItem>
        <MenuItem onClick={handleDischarge}>Discharge</MenuItem>
      </Menu>

      <Dialog
        open={!!viewingOrder}
        onClose={() => {
          setViewingOrder(null);
          setViewingId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.3rem" }}>
          Care Plan Details
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {viewingOrder && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Resident Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {viewingOrder?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Physician
                    </Typography>
                    <Typography variant="body1">
                      {viewingOrder?.attendingPhysician}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
  <Typography variant="subtitle2" color="textSecondary">
    Status
  </Typography>
  <Chip
    label={viewingOrder?.status.replace("_", " ")}
    color={
      viewingOrder?.status === "ACTIVE" || viewingOrder?.status === "Active"
        ? "success"
        : viewingOrder?.status === "ON_LEAVE"
        ? "warning"
        : viewingOrder?.status === "PENDING" || viewingOrder?.status === "Pending"
        ? "info"
        : viewingOrder?.status === "DISCHARGED" || viewingOrder?.status === "Discharged"
        ? "error"
        : viewingOrder?.status === "TRANSFERRED" || viewingOrder?.status === "Transferred"
        ? "secondary"
        : "default"
    }
    size="small"
  />
</Grid>
                </Grid>
              </Paper>

              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "#2CA6A4", mt: 2, mb: 2 }}
              >
                Care Plan Sections
              </Typography>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Dietary Restrictions
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    {viewingOrder?.carePlan?.dietary ||
                      "No dietary restrictions recorded."}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Daily Activities
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    {viewingOrder?.carePlan?.activities ||
                      "No daily activities recorded."}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Ambulation
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    {viewingOrder?.carePlan?.ambulation ||
                      "No ambulation details recorded."}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Wound Care/Skin Check
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    {viewingOrder?.carePlan?.woundCare ||
                      "No wound care details recorded."}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setViewingOrder(null);
              setViewingId(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {viewingMedicationId && (
        <MedicationDialog
          open={!!viewingMedicationId}
          residentId={viewingMedicationId}
          onClose={() => setViewingMedicationId(null)}
        />
      )}
    </Box>
  );
};

const MedicationDialog: React.FC<{
  open: boolean;
  residentId: string;
  onClose: () => void;
}> = ({ open, residentId, onClose }) => {
  const { medications, loading, fetchMedicationsByResident } = useMedications({
    residentId,
  });

  useEffect(() => {
    if (open && residentId) {
      fetchMedicationsByResident(residentId);
    }
  }, [open, residentId, fetchMedicationsByResident]);

  // Helper function to safely access medication properties
  const getMedicationProperty = (med: any, property: string): string => {
    return med?.[property] || "N/A";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Medication Administration Record</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : medications.length === 0 ? (
          <Typography>No medications found for this resident.</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Medication Name</TableCell>
                  <TableCell>Dosage</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {medications.map((med: any) => (
                  <TableRow key={med.id || Math.random()}>
                    <TableCell>
                      {getMedicationProperty(med, "medicationName")}
                    </TableCell>
                    <TableCell>
                      {getMedicationProperty(med, "dosage")}
                    </TableCell>
                    <TableCell>
                      {getMedicationProperty(med, "frequency")}
                    </TableCell>
                    <TableCell>{getMedicationProperty(med, "route")}</TableCell>
                    <TableCell>
                      {getMedicationProperty(med, "startDate")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getMedicationProperty(med, "status")}
                        size="small"
                        color={getMedicationProperty(med, "status") === "ACTIVE" ? "success" : "default"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResidentsPage;
